import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Loader2, GraduationCap, Users } from "lucide-react";

export default function StepApprendisti({ dvr, data, onChange, aiLoading, setAiLoading }) {
  const d = data || {};
  const s1 = dvr.steps?.step1_dati_generali || {};
  const [note, setNote] = useState(d._note || "");

  const run = async () => {
    setAiLoading(true);
    try {
      const r = await api.post("/dvr/ai/apprendisti", {
        ateco: s1.ateco,
        tipo_attivita: s1.tipo_attivita,
        numero_apprendisti: s1.numero_apprendisti,
        note,
      }, { timeout: 120000 });
      onChange({ ...r.data, _note: note });
      toast.success("Valutazione apprendisti completata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI error");
    }
    setAiLoading(false);
  };

  const B = ({ title, obj, keys }) => obj ? (
    <div className="border border-border rounded-md p-3">
      <h4 className="font-semibold text-sm mb-1.5">{title}</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
        {keys.map((k) => obj[k] && (
          <div key={k}>
            <span className="text-[11px] uppercase text-muted-foreground">{k.replace(/_/g, " ")}</span>
            <div>{obj[k]}</div>
          </div>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <div className="space-y-4" data-testid="step-apprendisti">
      <p className="text-sm text-muted-foreground">
        Valutazione ex <b>D.Lgs. 81/2015 (Testo unico apprendistato)</b> art. 41-47. Apprendisti in
        azienda: <b>{s1.numero_apprendisti || 0}</b>. Il modulo va comunque compilato per predisporre
        la corretta gestione anche di eventuali assunzioni future.
      </p>
      <Textarea
        rows={2}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Note (tipologia apprendistato: I qualifica, II professionalizzante, III alta formazione…)"
        data-testid="ap-note"
      />
      <Button onClick={run} disabled={aiLoading} className="rounded-full" data-testid="ap-run-ai">
        {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <GraduationCap className="w-4 h-4 mr-2" />}
        Valuta apprendisti
      </Button>

      <B title="Tipologia apprendistato" obj={d.tipologia_apprendistato} keys={["tipo", "descrizione", "normativa"]} />
      <B title="Formazione base/trasversale" obj={d.formazione_base_trasversale} keys={["ore_annuali", "modalita", "durata_contratto_max", "normativa"]} />
      <B title="Formazione professionalizzante" obj={d.formazione_professionalizzante} keys={["ore_totali", "modalita", "normativa"]} />
      <B title="Formazione sicurezza" obj={d.formazione_sicurezza} keys={["generale_ore", "specifica_ore", "normativa"]} />

      {d.tutor_aziendale && (
        <div className="border border-border rounded-md p-3 bg-muted/20">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4" />
            <h4 className="font-semibold text-sm">Tutor aziendale</h4>
            <span className={`text-[10px] px-2 py-0.5 rounded ${d.tutor_aziendale.obbligo ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-700"}`}>
              {d.tutor_aziendale.obbligo ? "OBBLIGATORIO" : "facoltativo"}
            </span>
          </div>
          {d.tutor_aziendale.requisiti?.length > 0 && (
            <ul className="text-sm space-y-0.5">
              {d.tutor_aziendale.requisiti.map((r, i) => <li key={`treq-${i}-${(r || "").slice(0, 20)}`}>• {r}</li>)}
            </ul>
          )}
          <div className="text-[11px] text-muted-foreground mt-1">Rif: {d.tutor_aziendale.normativa}</div>
        </div>
      )}

      {d.sorveglianza_sanitaria && (
        <div className="text-sm border border-border rounded-md p-3">
          <b>Sorveglianza sanitaria:</b> {d.sorveglianza_sanitaria.obbligo}
          {d.sorveglianza_sanitaria.note && <div className="text-xs mt-1">{d.sorveglianza_sanitaria.note}</div>}
          <div className="text-[11px] text-muted-foreground">Rif: {d.sorveglianza_sanitaria.normativa}</div>
        </div>
      )}

      {d.documenti_da_produrre?.length > 0 && (
        <div className="text-xs text-muted-foreground">Documenti: {d.documenti_da_produrre.join(" · ")}</div>
      )}
      {d.sintesi && <div className="text-sm text-muted-foreground italic">{d.sintesi}</div>}
    </div>
  );
}
